/**
 * Typed client for the scheduled-message endpoints.
 * Mirrors the ScheduledMessageDto record from the BFF.
 */

const baseUrl = import.meta.env.VITE_CONNECT_BFF_BASE_URL as string;

if (!baseUrl) throw new Error("VITE_CONNECT_BFF_BASE_URL is required.");

export interface ScheduledMessageDto {
    id: string;
    channelId: string;
    authorUserId: string;
    bodyHtml: string;
    sendAt: string;       // ISO-8601
    status: string;       // "Pending" | "Sent" | "Cancelled" | "Failed"
    createdAt: string;    // ISO-8601
}

async function fetchJson<T>(
    path: string,
    token: string | undefined,
    init: RequestInit = {}
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    if (!res.ok) throw new Error(`scheduledApi ${path} → ${res.status}`);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export const scheduledApi = {
    /**
     * Create a new scheduled channel message.
     * @param channelId  Target channel GUID.
     * @param bodyHtml   Already-rendered HTML body.
     * @param sendAt     Local Date object — converted to UTC ISO string.
     * @param token      Bearer access token.
     */
    createScheduled: (
        channelId: string,
        bodyHtml: string,
        sendAt: Date,
        token: string | undefined
    ) =>
        fetchJson<ScheduledMessageDto>(
            `/api/channels/${channelId}/messages/schedule`,
            token,
            {
                method: "POST",
                body: JSON.stringify({ bodyHtml, sendAt: sendAt.toISOString() }),
            }
        ),

    /**
     * List pending scheduled messages for the current user in a channel.
     */
    listScheduled: (channelId: string, token: string | undefined) =>
        fetchJson<ScheduledMessageDto[]>(
            `/api/channels/${channelId}/messages/schedule`,
            token
        ),

    /**
     * Cancel a pending scheduled message.
     */
    cancelScheduled: (
        channelId: string,
        id: string,
        token: string | undefined
    ) =>
        fetchJson<void>(
            `/api/channels/${channelId}/messages/schedule/${id}`,
            token,
            { method: "DELETE" }
        ),
};
